import React from 'react';
import * as logger from '@happyreturns/jscommons';
import { useMediaQuery } from 'react-responsive';
import PropTypes from 'prop-types';
import Icon from '../icons';
import {
  getColor,
  getCarrierTrackingPageURL,
  humanReadableName,
} from '../utility';
import {
  getMonthShort,
  getMonthLong,
  getWeekdayLong,
  getDate,
} from '../datetime';
import { logVisit } from '../analytics';
import { TrackingStatus } from '../status/types';
import statusMapper from '../status/mapper';

function TrackingBox(props) {
  function renderProgressTriad(leftActive, centerActive, rightActive) {
    const isMobile = useMediaQuery({ query: '(max-width: 979px)' });

    return (
      <div className="progress-triad" style={{ 'margin-top': '40px' }}>
        <Icon
          className="progress-triad-icon"
          fill={getColor(leftActive ? 'primary' : 'gray-light')}
          name={isMobile ? 'progressTriadLeftMobile' : 'progressTriadLeft'}
        />
        <Icon
          className="progress-triad-icon"
          fill={getColor(centerActive ? 'primary' : 'gray-light')}
          name={isMobile ? 'progressTriadCenterMobile' : 'progressTriadCenter'}
        />
        <Icon
          className="progress-triad-icon"
          fill={getColor(rightActive ? 'primary' : 'gray-light')}
          name={isMobile ? 'progressTriadRightMobile' : 'progressTriadRight'}
        />
      </div>
    );
  }

  function renderStatusDelivered() {
    // if no arrival date is supplied and the shipment has been delivered, use the estimated arrival date instead
    const arrivalDate = new Date(
      props.trackingInfo.arrival || props.trackingInfo.estimated_arrival,
    );

    return (
      <>
        <Icon
          className="checkmark"
          style={{ 'align-self': 'center', 'margin-top': '40px' }}
          fill={getColor('grayish')}
          name="checkmark"
        />
        {props.returnMode ? (
          <div
            className="tracking-text-header3"
            style={{ 'margin-top': '32px' }}
          >
            Your Return Has Been Received
          </div>
        ) : (
          <div
            className="tracking-text-header3"
            style={{ 'margin-top': '32px' }}
          >
            Your Shipment Has Been Delivered!
          </div>
        )}
        {renderProgressTriad(true, true, true)}
        {props.returnMode ? (
          <>
            <div className="tracking-text-message">
              Received on {getWeekdayLong(arrivalDate)},{' '}
              {getMonthShort(arrivalDate)} {getDate(arrivalDate)}
            </div>
            <div className="tracking-text-submessage">
              You’ll receive an email summary of your refund after your item(s)
              are processed.
            </div>
          </>
        ) : (
          <>
            <div className="tracking-text-message">
              Delivered on {getWeekdayLong(arrivalDate)},{' '}
              {getMonthShort(arrivalDate)} {getDate(arrivalDate)}
            </div>
            <div className="tracking-text-submessage">
              Shipped with {humanReadableName(props.carrier)}
            </div>
          </>
        )}
      </>
    );
  }

  function renderStatusInTransit() {
    const estimatedArrivalDate = new Date(props.trackingInfo.estimated_arrival);

    return (
      <>
        {props.returnMode ? (
          <div className="tracking-text-title" style={{ 'margin-top': '40px' }}>
            RETURN EXPECTED
          </div>
        ) : (
          <div className="tracking-text-title" style={{ 'margin-top': '40px' }}>
            ESTIMATED DELIVERY
          </div>
        )}
        <div className="tracking-text-header1" style={{ 'margin-top': '16px' }}>
          {getWeekdayLong(estimatedArrivalDate)}
        </div>
        <div className="tracking-text-header2">
          {getMonthLong(estimatedArrivalDate)} {getDate(estimatedArrivalDate)}
        </div>
        {renderProgressTriad(true, true, false)}
        <div className="tracking-text-message">In Transit</div>
        {props.returnMode ? (
          <div className="tracking-text-submessage">
            Your refund will be initiated after your return has been received.
          </div>
        ) : (
          <div className="tracking-text-submessage">
            Shipped with {humanReadableName(props.carrier)}
          </div>
        )}
      </>
    );
  }

  function renderStatusInTransitNoEstimatedArrivalDate() {
    return (
      <>
        {props.returnMode ? (
          <div className="tracking-text-title" style={{ 'margin-top': '40px' }}>
            RETURN STATUS
          </div>
        ) : (
          <div className="tracking-text-title" style={{ 'margin-top': '40px' }}>
            SHIPMENT STATUS
          </div>
        )}
        <div className="tracking-text-header1" style={{ 'margin-top': '16px' }}>
          In
        </div>
        <div className="tracking-text-header2">Transit</div>
        {renderProgressTriad(true, true, false)}
        {props.returnMode ? (
          <div className="tracking-text-message">
            Return date estimate available soon.
          </div>
        ) : (
          <div className="tracking-text-message">
            Delivery date estimate available soon.
          </div>
        )}
        <div className="tracking-text-submessage">
          Shipped with {humanReadableName(props.carrier)}
        </div>
      </>
    );
  }

  function renderStatusPreparing() {
    if (props.returnMode) {
      return (
        <>
          <div className="tracking-text-title" style={{ 'margin-top': '40px' }}>
            RETURN STATUS
          </div>
          <div
            className="tracking-text-header1"
            style={{ 'margin-top': '16px' }}
          >
            Label
          </div>
          <div className="tracking-text-header2">Created</div>
          {renderProgressTriad(true, false, false)}
          <div className="tracking-text-message">
            Return label has been created
          </div>
          <div className="tracking-text-submessage">
            Updates will be provided here once the carrier has received your
            return.
          </div>
        </>
      );
    } else {
      return (
        <>
          <div className="tracking-text-title" style={{ 'margin-top': '40px' }}>
            SHIPMENT STATUS
          </div>
          <div
            className="tracking-text-header1"
            style={{ 'margin-top': '16px' }}
          >
            Preparing
          </div>
          <div className="tracking-text-header2">Shipment</div>
          {renderProgressTriad(true, false, false)}
          <div className="tracking-text-message">
            Your shipment will be shipped shortly
          </div>
          <div className="tracking-text-submessage">
            Shipped with {humanReadableName(props.carrier)}
          </div>
        </>
      );
    }
  }

  function renderError() {
    return (
      <>
        {props.returnMode ? (
          <>
            <div className="tracking-error">
              <div className="tracking-error-text">
                Return updates temporarily unavailable. Please try again
                shortly.
              </div>
            </div>
            <div
              className="tracking-text-title"
              style={{ 'margin-top': '20px' }}
            >
              RETURN EXPECTED
            </div>
          </>
        ) : (
          <>
            <div className="tracking-error">
              <div className="tracking-error-text">
                Shipment updates temporarily unavailable. Please try again
                shortly.
              </div>
            </div>
            <div
              className="tracking-text-title"
              style={{ 'margin-top': '20px' }}
            >
              ESTIMATED DELIVERY
            </div>
          </>
        )}
        <div className="tracking-text-header1" style={{ 'margin-top': '16px' }}>
          Temporarily
        </div>
        <div className="tracking-text-header2">Unable to Display</div>
        {renderProgressTriad(false, false, false)}
        <div className="tracking-text-message">
          Status: Temporarily unable to display
        </div>
        <div className="tracking-text-submessage">
          Shipped with {humanReadableName(props.carrier)}
        </div>
      </>
    );
  }

  function renderCancelled() {
    return (
      <>
        {props.returnMode ? (
          <>
            <div className="tracking-error">
              <div className="tracking-error-text">
                Your return has been cancelled. Please click{' '}
                <a
                  href={getCarrierTrackingPageURL(
                    props.carrier,
                    props.tracking,
                    props.trackingInfo,
                  )}
                >
                  here
                </a>{' '}
                for details.
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="tracking-error">
              <div className="tracking-error-text">
                This shipment has been cancelled. Please click{' '}
                <a
                  href={getCarrierTrackingPageURL(
                    props.carrier,
                    props.tracking,
                    props.trackingInfo,
                  )}
                >
                  here
                </a>{' '}
                for details.
              </div>
            </div>
          </>
        )}
        <div className="tracking-text-header1" style={{ 'margin-top': '16px' }}>
          Temporarily
        </div>
        <div className="tracking-text-header2">Unable to Display</div>
        {renderProgressTriad(false, false, false)}
        <div className="tracking-text-message" style={{ color: '#DF3719' }}>
          Status: Cancelled
        </div>
        <div className="tracking-text-submessage">
          Shipped with {humanReadableName(props.carrier)}
        </div>
      </>
    );
  }

  function renderException() {
    return (
      <>
        {props.returnMode ? (
          <>
            <div
              className="tracking-error"
              data-testid="tracking-box-exception"
            >
              {/* Only show an underlined "contact us" link if the contact url is configured */}
              <div className="tracking-error-text">
                An issue has occurred with your return. Please click{' '}
                <a
                  href={getCarrierTrackingPageURL(
                    props.carrier,
                    props.tracking,
                    props.trackingInfo,
                  )}
                >
                  here
                </a>{' '}
                for details. If the issue persists please{' '}
                {STYLES.contact_url ? (
                  <a href={STYLES.contact_url}>contact us</a>
                ) : (
                  'contact us'
                )}
                .
              </div>
            </div>
            <div
              className="tracking-text-title"
              style={{ 'margin-top': '20px' }}
            >
              RETURN EXPECTED
            </div>
          </>
        ) : (
          <>
            <div
              className="tracking-error"
              data-testid="tracking-box-exception"
            >
              {/* Only show an underlined "contact us" link if the contact url is configured */}
              <div className="tracking-error-text">
                An issue has occurred with your shipment. Please click{' '}
                <a
                  href={getCarrierTrackingPageURL(
                    props.carrier,
                    props.tracking,
                    props.trackingInfo,
                  )}
                >
                  here
                </a>{' '}
                for details. If the issue persists please{' '}
                {STYLES.contact_url ? (
                  <a href={STYLES.contact_url}>contact us</a>
                ) : (
                  'contact us'
                )}
                .
              </div>
            </div>
            <div
              className="tracking-text-title"
              style={{ 'margin-top': '20px' }}
            >
              ESTIMATED DELIVERY
            </div>
          </>
        )}
        <div className="tracking-text-header1" style={{ 'margin-top': '16px' }}>
          Temporarily
        </div>
        <div className="tracking-text-header2">Unable to Display</div>
        {renderProgressTriad(false, false, false)}
        {STYLES.contact_url ? (
          <div className="tracking-text-message" style={{ color: '#DF3719' }}>
            Status: Please contact us to review
          </div>
        ) : (
          <div className="tracking-text-message" style={{ color: '#DF3719' }}>
            Status: Unavailable
          </div>
        )}
        <div className="tracking-text-submessage">
          Shipped with {humanReadableName(props.carrier)}
        </div>
      </>
    );
  }

  // renders a core status message that tells the user where there package is, etc
  function renderStatus() {
    // Map the status for the given carrier and tracking info
    const mappedStatus = statusMapper(props.carrier, props.trackingInfo);

    // render and log the appropriate display for the given status
    switch (mappedStatus) {
      case TrackingStatus.Unknown:
        logVisit(props.carrier, props.tracking, 'unknown');
        return renderError();
      case TrackingStatus.Exception:
        logVisit(props.carrier, props.tracking, 'exception');
        return renderException();
      case TrackingStatus.InTransit:
        logVisit(props.carrier, props.tracking, 'intransit');
        return renderStatusInTransit();
      case TrackingStatus.InTransitNoEstimation:
        logVisit(props.carrier, props.tracking, 'intransit');
        return renderStatusInTransitNoEstimatedArrivalDate();
      case TrackingStatus.Delivered:
        logVisit(props.carrier, props.tracking, 'delivered');
        return renderStatusDelivered();
      case TrackingStatus.Cancelled:
        // This visit cannot be logged currently do to a missing
        // status in the analytics db
        logVisit(props.carrier, props.tracking, 'cancelled');
        return renderCancelled();
      default:
        logVisit(props.carrier, props.tracking, 'preparing');
        return renderStatusPreparing();
    }
  }

  return (
    <div className="tracking-box infobox">
      {renderStatus()}
      <div
        className="tracking-box-tracking"
        onClick={() => {
          logger.Info('User clicked tracking tracking number section');

          // open page specified by prop in a new tab
          const url = getCarrierTrackingPageURL(
            props.carrier,
            props.tracking,
            props.trackingInfo,
          );
          url && window.open(url, '_blank');
        }}
      >
        <div className="tracking-box-tracking-text">
          {humanReadableName(props.carrier)} Tracking: {props.tracking}
        </div>
      </div>
    </div>
  );
}
TrackingBox.propTypes = {
  // one of the carrier IDs supported by our shipping service, e.g. "usps", "fedex",
  carrier: PropTypes.string.isRequired,

  // tracking # of the package whose status is shown here
  tracking: PropTypes.string.isRequired,

  // when this is true, show messaging appropriate for when the customer
  // is returning a package to a retailer
  returnMode: PropTypes.bool.isRequired,

  // shipping.TrackingInfo object formatted in JSON
  // As of May 2020 this struct looks like so:
  // type TrackingInfo struct {
  //   Tracking         string     `json:"tracking"`
  //   Status           string     `json:"status"`
  //   Departure        *time.Time `json:"departure"`
  //   EstimatedArrival *time.Time `json:"estimated_arrival"`
  //   Arrival          *time.Time `json:"arrival"`
  // }
  // check in shipping's models.go to see the latest....
  //
  // if this is undefined, then an "unable to track" system error page will be shown
  trackingInfo: PropTypes.object,
};

export default TrackingBox;
